import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "gadtech-theme";

/**
 * v3.5v — Multiple colour themes on top of the light/dark base. Each option
 * maps to a `data-theme` attribute on <html>; index.css defines the palette.
 * Add a new theme by extending THEMES + the matching :root[data-theme="..."]
 * block in index.css.
 */
export const THEMES = [
  { id: "light",      label: "Light",       hint: "White + orange (default)",  swatch: "#F57C00", bg: "#F5F5F7" },
  { id: "dark",       label: "Dark",        hint: "Smoked steel + orange",     swatch: "#F57C00", bg: "#1B1B1B" },
  { id: "midnight",   label: "Midnight",    hint: "Deep blue command centre",  swatch: "#3B82F6", bg: "#0B1220" },
  { id: "emerald",    label: "Emerald",     hint: "Safety-green industrial",   swatch: "#10B981", bg: "#0B1A14" },
  { id: "slate",      label: "Steel Slate", hint: "Cool blue-grey dark",       swatch: "#94A3B8", bg: "#111827" },
  { id: "concrete",   label: "Concrete",    hint: "Warm neutral grey",         swatch: "#8B8680", bg: "#F5F1EB" },
  { id: "amber",      label: "Amber",       hint: "Warm caution-yellow",       swatch: "#F59E0B", bg: "#1C1608" },
];

function applyTheme(theme) {
  if (!theme || theme === "light") document.documentElement.removeAttribute("data-theme");
  else document.documentElement.setAttribute("data-theme", theme);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "light"; }
    catch { return "light"; }
  });

  useEffect(() => { applyTheme(theme); }, [theme]);

  const setTheme = useCallback((t) => {
    setThemeState(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* ignore */ }
  }, []);

  // Keep the old two-way toggle for legacy buttons that only flip light/dark.
  const toggle = useCallback(
    () => setTheme(theme === "dark" ? "light" : "dark"),
    [theme, setTheme]
  );

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
